/* Bluewud search analytics.
 *
 * WHY THIS EXISTS, and why Shopify's built-in search report is not enough:
 * bluewud-search-intent.js routes category and budget queries to collections,
 * and bluewud-sku-search.js routes SKU hits straight to a product page. Those
 * searches never load /search, so Shopify's "top online store searches" report
 * cannot see them. Hooking the SUBMIT instead of the results page captures
 * every search regardless of where it lands.
 *
 * Where the data goes (all optional, each guarded):
 *   1. window.dataLayer  -> GTM, which the store already loads. From there it
 *      can be forwarded to GA4 as a `search` event and used to build Google
 *      Ads remarketing audiences.
 *   2. gtag('event','search') -> GA4 directly, if gtag is present.
 *   3. Shopify's own analytics.publish, if the storefront exposes it.
 *   4. localStorage ring buffer -> powers on-site "recent searches" and lets
 *      anyone read the last 50 searches from their own browser console during
 *      QA without waiting for a reporting pipeline.
 *
 * Only the search term and derived intent are recorded. No customer identity,
 * no email, no order data.
 */
(function () {
  'use strict';

  if (window.__bwSearchAnalytics) return;
  window.__bwSearchAnalytics = true;

  var STORE_KEY = 'bwSearchLog';
  var STORE_MAX = 50;

  function push(payload) {
    /* 1. GTM dataLayer */
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(
        Object.assign({ event: 'bluewud_search' }, payload)
      );
    } catch (e) {}

    /* 2. GA4 recommended `search` event */
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'search', {
          search_term: payload.term,
          bw_intent: payload.intent,
          bw_destination: payload.destination,
        });
      }
    } catch (e) {}

    /* 3. Shopify storefront analytics, when available */
    try {
      if (window.analytics && typeof window.analytics.publish === 'function') {
        window.analytics.publish('bluewud_search', payload);
      }
    } catch (e) {}

    /* 4. Local ring buffer for QA + on-site recent searches */
    try {
      var log = JSON.parse(localStorage.getItem(STORE_KEY) || '[]');
      log.unshift(payload);
      localStorage.setItem(STORE_KEY, JSON.stringify(log.slice(0, STORE_MAX)));
    } catch (e) {}
  }

  /* Classify what the shopper meant, so the report separates "we have no such
     product" from "our search could not find the product we do have". */
  function classify(term) {
    var t = String(term || '').trim();
    if (!t) return 'empty';
    if (window.__bwIsSkuQuery && window.__bwIsSkuQuery(t)) return 'sku';
    if (/\b(under|below|within|upto|up to|less than)\b/i.test(t) || /\d{3,}/.test(t)) return 'budget';
    if (/\s/.test(t)) return 'phrase';
    return 'keyword';
  }

  /* ---- Every search submit, wherever it happens ---------------------- */
  document.addEventListener(
    'submit',
    function (e) {
      try {
        var form = e.target;
        if (!form || !form.matches) return;
        var action = form.getAttribute('action') || '';
        if (action.indexOf('/search') === -1 && !form.hasAttribute('data-live-search-form')) return;

        var input = form.querySelector('input[name="q"]');
        var term = input ? input.value.trim() : '';
        if (!term) return;

        push({
          term: term,
          term_lc: term.toLowerCase(),
          intent: classify(term),
          destination: 'pending',
          page: location.pathname,
          ts: new Date().toISOString(),
        });
      } catch (err) {
        /* analytics must never break a search */
      }
    },
    true
  );

  /* ---- Result counts, including the zero-result case ------------------
     Zero-result searches are the most actionable row in the whole report:
     they are customers telling you, in their own words, what they wanted and
     did not get. Recorded as its own event so it can be alerted on. */
  function reportResults() {
    try {
      if (location.pathname.indexOf('/search') !== 0) return;
      var params = new URLSearchParams(location.search);
      var raw = params.get('bwraw') || params.get('bwsku') || params.get('q') || '';
      if (!raw) return;

      var count = document.querySelectorAll('.productgrid--items > .productgrid--item').length;

      push({
        term: raw,
        term_lc: raw.toLowerCase(),
        intent: classify(raw),
        destination: 'search_results',
        results: count,
        zero_results: count === 0,
        page: location.pathname,
        ts: new Date().toISOString(),
      });
    } catch (err) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reportResults);
  } else {
    reportResults();
  }

  /* ---- Read the log during QA: bluewudSearchLog() -------------------- */
  window.bluewudSearchLog = function () {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY) || '[]');
    } catch (e) {
      return [];
    }
  };
})();
